import { ExpandableContent } from "@nova/component-expandable-content"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import { MemberExtended } from "@nova/schemas"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { AddOnIcon } from "plugins/AddonIcon"
import { useMemo } from "react"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"
import { selectTab, setTab } from "./slice"
import * as Icons from "@nova/component-icons"
import { useLastOnlineDate } from "./usePlatformMetrics"
import {
    currencyStateColor,
    summarizeCurrencyStatuses,
    usePilotCurrencyStatuses,
} from "features/pilot-tracking/currency/usePilotCurrencyStatuses"

const PilotCurrencyStat = ({ userId }: { userId: string }) => {
    const { statusesByUser } = usePilotCurrencyStatuses()
    const statuses = statusesByUser.get(userId)
    if (!statuses?.length) return null

    const summary = summarizeCurrencyStatuses(statuses)
    return (
        <div className="flex flex-col shrink-0">
            <small className="text-gray-400">Currency</small>
            <small className="flex items-center gap-1 text-gray-100">
                <Icons.SmallIcon
                    Icon={Icons.BrowseGallery}
                    className={currencyStateColor[summary.state]}
                />
                {summary.label}
            </small>
        </div>
    )
}

const PilotPageTabSwitcher = () => {
    const dispatch = useAppDispatch()
    const tab = useAppSelector(selectTab)
    return (
        <div className="flex">
            <TabHeader
                currentMode={tab}
                mode="overview"
                setActiveMode={() => dispatch(setTab("overview"))}
                title="Overview"
            />
            <TabHeader
                currentMode={tab}
                mode="history"
                setActiveMode={() => dispatch(setTab("history"))}
                title="History"
            />
            <div className="flex-1 border-b-2 border-b-white-200" />
        </div>
    )
}

export const PilotPageHeader = ({ user }: { user: MemberExtended }) => {
    const addons = useMemo(() => Object.values(user.addOns).filter(Boolean), [user.addOns])
    const { formattedLastOnlineString } = useLastOnlineDate({ pilotId: user.id! })
    return (
        <>
            <div className="flex gap-8 text-gray-100 pt-12 items-end">
                <div className="shrink-0 w-[134px] aspect-square rounded-2xl overflow-hidden flex items-center justify-center">
                    {!!user.profilePhotoS3Key ? (
                        <ProfilePhoto userId={user.id ?? ""} fullName={user.fullName ?? ""} />
                    ) : (
                        <Icons.NoProfilePicture className="w-full h-full text-gray-700/80" />
                    )}
                </div>
                <div className="flex flex-col justify-end gap-2 min-w-0">
                    <div className="flex items-center gap-3">
                        <LicenseBadge license={user.license.subscriptionType} />
                        <div className="flex items-center gap-2">
                            {addons.map(
                                (addOn) =>
                                    addOn?.subscriptionType && (
                                        <AddOnIcon
                                            key={addOn.licenseId}
                                            addOn={addOn.subscriptionType}
                                        />
                                    )
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col justify-end">
                        <h1 className="text-[42px] telegraf text-white truncate leading-none pb-1.5">
                            {user.fullName}
                        </h1>
                        {user.title && <h3 className="truncate pb-1.5">{user.title}</h3>}
                    </div>
                </div>
                <div className="flex-1 flex gap-x-4 gap-y-2 items-end justify-end flex-wrap pb-1.5">
                    <div className="flex flex-col justify-end gap-2 shrink-0 min-w-fit">
                        {!!user.phoneNumber && (
                            <div className="flex items-center gap-2">
                                <Icons.SmallIcon Icon={Icons.Call} />
                                {user.phoneNumber}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <Icons.SmallIcon Icon={Icons.Mail} />
                            {user.email}
                        </div>
                    </div>

                    <div className="flex gap-x-4 ml-auto">
                        <PilotCurrencyStat userId={user.id!} />

                        <div className="flex flex-col shrink-0">
                            <small className="text-gray-400">Last online</small>
                            <small>{formattedLastOnlineString ?? "--"}</small>
                        </div>

                        <div className="flex flex-col shrink-0">
                            <small className="text-gray-400">Joined on</small>
                            <small>{new Date(user.createdAt ?? 0).toDateString()}</small>
                        </div>
                    </div>
                </div>
            </div>
            <div className="pt-4 pb-6">
                <PilotPageTabSwitcher />
            </div>
        </>
    )
}

export const PilotPageStickyHeader = ({
    user,
    visible,
}: {
    user: MemberExtended
    visible: boolean
}) => {
    const addons = useMemo(() => Object.values(user.addOns).filter(Boolean), [user.addOns])
    return (
        <ExpandableContent isOpen={visible}>
            <div className="relative bg-black-500 py-6">
                <div className="absolute inset-0 backdrop-blur-md" />
                <div className="relative z-10">
                    <div className="flex gap-6">
                        <div className="w-[74px] min-w-[74px] aspect-square rounded-md overflow-hidden">
                            {!!user.profilePhotoS3Key ? (
                                <ProfilePhoto
                                    userId={user.id ?? ""}
                                    fullName={user.fullName ?? ""}
                                />
                            ) : (
                                <Icons.NoProfilePicture className="w-full h-full text-gray-700/80" />
                            )}
                        </div>
                        <div className="flex flex-col justify-end gap-4">
                            <div className="flex gap-3 items-center">
                                <LicenseBadge license={user.license.subscriptionType} />
                                <div className="flex items-center">
                                    {addons.map(
                                        (addOn) =>
                                            addOn?.subscriptionType && (
                                                <AddOnIcon
                                                    key={addOn.licenseId}
                                                    addOn={addOn.subscriptionType}
                                                />
                                            )
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex flex-col gap-1">
                                    <div className="text-[21px] telegraf text-white">
                                        {user.fullName}
                                    </div>
                                    <div className="text-gray-300">{user.title}</div>
                                </div>
                                <div className="flex flex-col gap-1 justify-end h-full">
                                    {!!user.phoneNumber && (
                                        <small className="text-gray-100">{user.phoneNumber}</small>
                                    )}
                                    <small className="text-gray-100">{user.email}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="pt-3">
                        <PilotPageTabSwitcher />
                    </div>
                </div>
            </div>
        </ExpandableContent>
    )
}
